// DateRangeLite.jsx
import * as React from "react";
import { useState, useMemo } from "react";
import {
    Stack,
    Button,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    Chip,
    Menu,
    MenuItem,
    Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import "moment/locale/pt-br";

/**
 * Date Range com menu de presets + dialog de personalização (sem pacote PRO).
 *
 * Props:
 * - initialStart: moment | null
 * - initialEnd: moment | null
 * - maxDays: number | null (limite do tamanho do intervalo; ex: 31) - opcional
 * - disablePast: boolean - opcional
 * - onChangeRange: (range: { start: moment|null, end: moment|null, startISO: string|null, endISO: string|null }) => void
 * - buttonVariant: 'text' | 'outlined' | 'contained' - opcional
 */
export default function DateRangeLite({
                                          initialStart = moment().startOf("month"),
                                          initialEnd = moment().endOf("month"),
                                          maxDays = null,
                                          disablePast = false,
                                          onChangeRange,
                                          buttonVariant = "outlined",
                                      }) {
    // estado "oficial" do componente
    const [start, setStart] = useState(initialStart);
    const [end, setEnd] = useState(initialEnd);

    // estado temporário do dialog (rascunho)
    const [draftStart, setDraftStart] = useState(start);
    const [draftEnd, setDraftEnd] = useState(end);
    const [dialogOpen, setDialogOpen] = useState(false);

    // estado do menu suspenso
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    // normaliza (fim >= início)
    const normalize = (s, e) => {
        if (s && e && e.isBefore(s, "day")) return { s, e: s.clone() };
        return { s, e };
    };

    // aplica maxDays se houver
    const clampByMax = (s, e) => {
        // if (!maxDays || !s || !e) return { s, e };
        // const diff = e.diff(s, "days") + 1; // inclusivo
        // if (diff > maxDays) {
        //     return { s, e: s.clone().add(maxDays - 1, "days") };
        // }
        return { s, e };
    };

    // rascunho normalizado + limitado
    const draftComputed = useMemo(() => {
        const { s, e } = normalize(draftStart, draftEnd);
        return clampByMax(s, e);
    }, [draftStart, draftEnd, maxDays]);

    // resumo amigável
    const summary =
        start && end
            ? `${start.format("DD/MM/YYYY")} → ${end.format("DD/MM/YYYY")}`
            : "Selecione um intervalo";

    // aplica intervalo e dispara callback
    const setRange = (s, e) => {
        const { s: nS, e: nE } = clampByMax(...Object.values(normalize(s, e)));
        setStart(nS || null);
        setEnd(nE || null);
        if (onChangeRange) {
            onChangeRange({
                start: nS || null,
                end: nE || null,
                startISO: nS ? nS.format("YYYY-MM-DD") : null,
                endISO: nE ? nE.format("YYYY-MM-DD") : null,
            });
        }
    };

    // presets
    const applyPreset = (preset) => {
        switch (preset) {
            case "today":
                setRange(moment().startOf("day"), moment().endOf("day"));
                break;
            case "last7":
                setRange(moment().subtract(6, "days").startOf("day"), moment().endOf("day"));
                break;
            case "thisMonth":
                setRange(moment().startOf("month"), moment().endOf("month"));
                break;
            case "prevMonth":
                setRange(
                    moment().subtract(1, "month").startOf("month"),
                    moment().subtract(1, "month").endOf("month")
                );
                break;
            case "next7":
                setRange(moment().startOf("day"), moment().add(6, "days").endOf("day"));
                break;
            case "nextMonth":
                setRange(
                    moment().add(1, "month").startOf("month"),
                    moment().add(1, "month").endOf("month")
                );
                break;
            default:
                break;
        }
    };

    // dialog
    const openDialog = () => {
        setDraftStart(start);
        setDraftEnd(end);
        setDialogOpen(true);
    };
    const closeDialog = () => setDialogOpen(false);
    const applyDialog = () => {
        const { s, e } = draftComputed;
        setRange(s || null, e || null);
        setDialogOpen(false);
    };
    const clearRange = () => setRange(null, null);

    // menu
    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    // limites dinâmicos no dialog
    const minEndDate = draftComputed.s ? draftComputed.s.clone() : undefined;
    const maxStartDate = draftComputed.e ? draftComputed.e.clone() : undefined;
    const maxEndBySize =
        maxDays && draftComputed.s ? draftComputed.s.clone().add(maxDays - 1, "days") : undefined;

    return (
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="pt-br">
            <Stack spacing={1.5}>
                {/* Linha superior: resumo + ações */}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">


                    <Tooltip title="Selecionar período rápido">
                        <Button
                            id="range-menu-button"
                            aria-controls={menuOpen ? "range-menu" : undefined}
                            aria-haspopup="true"
                            aria-expanded={menuOpen ? "true" : undefined}
                            onClick={handleMenuClick}
                            variant={buttonVariant}
                            endIcon={<KeyboardArrowDownIcon />}
                            startIcon={<CalendarMonthIcon fontSize="small" />}
                            //size="small"
                            color={"secondary"}
                        >
                            {summary}
                        </Button>
                    </Tooltip>


                </Stack>

                {/* Menu suspenso com presets */}
                <Menu
                    id="range-menu"
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    MenuListProps={{ "aria-labelledby": "range-menu-button" }}
                >
                    <MenuItem
                        onClick={() => {
                            applyPreset("today");
                            handleMenuClose();
                        }}
                        sx={{fontSize:14}}
                    >
                        Hoje
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            applyPreset("last7");
                            handleMenuClose();
                        }}
                        sx={{fontSize:14}}
                    >
                        Últimos 7 dias
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            applyPreset("thisMonth");
                            handleMenuClose();
                        }}
                        sx={{fontSize:14}}
                    >
                        Este mês
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            applyPreset("prevMonth");
                            handleMenuClose();
                        }}
                        sx={{fontSize:14}}
                    >
                        Mês passado
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            applyPreset("nextMonth");
                            handleMenuClose();
                        }}
                        sx={{fontSize:14}}
                    >
                        Próximo Mês
                    </MenuItem>

                    <Divider />

                    <MenuItem
                        onClick={() => {
                            handleMenuClose();
                            openDialog();
                        }}
                        sx={{fontSize:14}}
                    >
                        Personalizar…
                    </MenuItem>
                </Menu>

                {/* Dialog de personalização */}
                <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                    <DialogTitle
                        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                    >
                        Personalizar intervalo
                        <IconButton onClick={closeDialog} size="small" aria-label="Fechar">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Selecione uma data inicial e final.

                        </Typography>

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <DatePicker
                                label="Início"
                                value={draftComputed.s}
                                onChange={(v) => setDraftStart(v)}
                                format="DD/MM/YYYY"
                                disablePast={disablePast}

                                slotProps={{ textField: { fullWidth: true } }}
                            />

                            <DatePicker
                                label="Fim"
                                value={draftComputed.e}
                                onChange={(v) => setDraftEnd(v)}
                                format="DD/MM/YYYY"
                                disablePast={disablePast}

                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </Stack>

                        <Typography variant="caption" sx={{ mt: 2, display: "block" }}>
                            Selecionado:{" "}
                            {draftComputed.s && draftComputed.e
                                ? `${draftComputed.s.format("DD/MM/YYYY")} → ${draftComputed.e.format("DD/MM/YYYY")}`
                                : "—"}
                        </Typography>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={closeDialog}>Cancelar</Button>
                        <Button onClick={applyDialog} variant="contained">
                            Aplicar
                        </Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        </LocalizationProvider>
    );
}
